export const AQUA_DRAW_MANIFEST = {
  app_id: "aqua_draw",
  display_name: "Aqua Draw",
  aqua_identity: "Aqua",
  integration_version: "0.22.6-guided-invoice-payments",
  welcome_text: "Welcome to Aqua Draw. I’m Aqua. What would you like to create?",
  purpose: "Inspect, navigate, enter information, and operate supported construction-draw workflows through registered semantic controls.",
  interaction_modes: ["direct_action", "guided_teaching", "simulation", "diagnostic"],
  capabilities: [
    "inspect_current_screen",
    "inspect_financial_health",
    "navigate_workspace",
    "open_client",
    "open_project_address",
    "open_sow_category",
    "open_exact_invoice",
    "open_exact_request_review",
    "highlight_registered_control",
    "teach_current_step",
    "navigate_history",
    "scroll_screen",
    "activate_registered_control",
    "fill_registered_field",
    "press_enter",
    "create_client_and_project",
    "create_project_address",
    "create_sow_category",
    "create_draw_request_with_confirmation",
    "mark_check_received_with_confirmation",
    "delete_record_with_confirmation",
    "open_diagnostics",
  ],
  destructive_actions_require_confirmation: true,
  external_actions_require_verified_receipt: true,
  physical_voice_status: "not_tested",
} as const;

export const AQUA_TOOLS = [
  {
    type: "function",
    name: "inspect_current_screen",
    description: "Read the current Aqua Draw screen, scoped invoice and holdback totals, registered controls, and available semantic record identifiers.",
    parameters: { type: "object", properties: {}, additionalProperties: false },
  },
  {
    type: "function",
    name: "navigate_workspace",
    description: "Open a registered owner workspace screen. This is a safe reversible navigation action.",
    parameters: {
      type: "object",
      properties: { destination: { type: "string", enum: ["dashboard", "clients", "actions", "settings", "diagnostics"] } },
      required: ["destination"],
      additionalProperties: false,
    },
  },
  {
    type: "function",
    name: "open_client",
    description: "Open one client using its stable client identifier from the current screen context.",
    parameters: {
      type: "object",
      properties: { client_id: { type: "string" } },
      required: ["client_id"],
      additionalProperties: false,
    },
  },
  {
    type: "function",
    name: "open_project_address",
    description: "Open one project address using its stable job identifier from the current screen context.",
    parameters: {
      type: "object",
      properties: { job_id: { type: "string" } },
      required: ["job_id"],
      additionalProperties: false,
    },
  },
  {
    type: "function",
    name: "open_sow_category",
    description: "Open one scope-of-work category using its stable SOW identifier from the current screen context.",
    parameters: {
      type: "object",
      properties: { sow_id: { type: "string" } },
      required: ["sow_id"],
      additionalProperties: false,
    },
  },
  {
    type: "function",
    name: "open_invoice",
    description: "Open one exact invoice using its stable request identifier. Use this to take the owner or homeowner directly to the requested invoice instead of stopping on a general invoice list.",
    parameters: {
      type: "object",
      properties: { request_id: { type: "string" } },
      required: ["request_id"],
      additionalProperties: false,
    },
  },
  {
    type: "function",
    name: "open_request_review",
    description: "Open one exact pending homeowner request review using its stable request identifier. In the client portal, use this to take the authenticated customer directly to the SOW decisions waiting for them.",
    parameters: {
      type: "object",
      properties: { request_id: { type: "string" } },
      required: ["request_id"],
      additionalProperties: false,
    },
  },
  {
    type: "function",
    name: "highlight_registered_control",
    description: "Visually highlight one real registered Aqua Draw control during teaching or diagnostics.",
    parameters: {
      type: "object",
      properties: { control_id: { type: "string" } },
      required: ["control_id"],
      additionalProperties: false,
    },
  },
  {
    type: "function",
    name: "teach_current_step",
    description: "Highlight one exact visible customer control and return its verified purpose and expected next screen result for a one-step-at-a-time walkthrough.",
    parameters: {
      type: "object",
      properties: { control_id: { type: "string" } },
      required: ["control_id"],
      additionalProperties: false,
    },
  },
  {
    type: "function",
    name: "navigate_history",
    description: "Move to the verified previous or next Aqua Draw location in the in-app navigation history.",
    parameters: { type: "object", properties: { direction: { type: "string", enum: ["back", "forward"] } }, required: ["direction"], additionalProperties: false },
  },
  {
    type: "function",
    name: "scroll_screen",
    description: "Scroll the active registered screen container up, down, exactly to the top, or exactly to the bottom.",
    parameters: { type: "object", properties: { direction: { type: "string", enum: ["up", "down", "top", "bottom"] } }, required: ["direction"], additionalProperties: false },
  },
  {
    type: "function",
    name: "activate_registered_control",
    description: "Activate one visible registered button or tab. Governed controls require the authenticated user’s confirmation and cannot bypass authorization.",
    parameters: { type: "object", properties: { control_id: { type: "string" } }, required: ["control_id"], additionalProperties: false },
  },
  {
    type: "function",
    name: "fill_registered_field",
    description: "Enter text or numeric information into one visible registered field. Field values are not added to durable memory.",
    parameters: { type: "object", properties: { field_id: { type: "string" }, value: { type: "string" } }, required: ["field_id", "value"], additionalProperties: false },
  },
  {
    type: "function",
    name: "press_enter",
    description: "Press Enter on the focused or specified registered field and submit its registered form when browser semantics permit it.",
    parameters: { type: "object", properties: { field_id: { type: "string" } }, additionalProperties: false },
  },
  {
    type: "function",
    name: "create_client_and_project",
    description: "Create a client and the client's first project address from complete owner-provided information, then verify both records.",
    parameters: { type: "object", properties: { name: { type: "string" }, company: { type: "string" }, email: { type: "string" }, phone: { type: "string" }, address: { type: "string" }, title: { type: "string" } }, required: ["name", "email", "phone", "address", "title"], additionalProperties: false },
  },
  {
    type: "function",
    name: "create_project_address",
    description: "Create a new project/job address for an existing client and verify the new job record.",
    parameters: { type: "object", properties: { client_id: { type: "string" }, address: { type: "string" }, title: { type: "string" } }, required: ["client_id", "address", "title"], additionalProperties: false },
  },
  {
    type: "function",
    name: "create_sow_category",
    description: "Create an SOW category for a registered job and verify its contract amount and record identifier.",
    parameters: { type: "object", properties: { job_id: { type: "string" }, title: { type: "string" }, description: { type: "string" }, contract_amount: { type: "number" } }, required: ["job_id", "title", "description", "contract_amount"], additionalProperties: false },
  },
  {
    type: "function",
    name: "create_draw_request",
    description: "Preview and request owner confirmation before creating a financial draw request. Photos must be attached manually in this build.",
    parameters: { type: "object", properties: { sow_id: { type: "string" }, amount: { type: "number" }, explanation: { type: "string" } }, required: ["sow_id", "amount", "explanation"], additionalProperties: false },
  },
  {
    type: "function",
    name: "mark_check_received",
    description: "Preview and request owner confirmation before marking a registered draw's check received.",
    parameters: { type: "object", properties: { draw_id: { type: "string" } }, required: ["draw_id"], additionalProperties: false },
  },
  {
    type: "function",
    name: "delete_record",
    description: "Preview and request explicit owner confirmation before deleting one client, job, SOW, or draw record and its governed descendants.",
    parameters: { type: "object", properties: { record_type: { type: "string", enum: ["client", "job", "sow", "draw"] }, record_id: { type: "string" } }, required: ["record_type", "record_id"], additionalProperties: false },
  },
] as const;


const CLIENT_TOOL_NAMES = new Set([
  "inspect_current_screen",
  "open_client",
  "open_project_address",
  "open_sow_category",
  "open_invoice",
  "open_request_review",
  "highlight_registered_control",
  "teach_current_step",
  "navigate_history",
  "scroll_screen",
  "activate_registered_control",
  "fill_registered_field",
  "press_enter",
]);

export function aquaToolsForContext(portalMode: boolean) {
  return portalMode ? AQUA_TOOLS.filter((tool) => CLIENT_TOOL_NAMES.has(tool.name)) : AQUA_TOOLS;
}
